import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PiTruck, PiWarningCircle } from 'react-icons/pi';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import ClientAvatars from '@v2/components/ui/ClientAvatars';
import SupplierAvatars from './SupplierAvatars';
import { ploomesDealPath } from './dealPath';
import type { SupplierThumb } from './types';

export type ImportPreviewGroup = {
    supplier_label: string;
    supplier_display: SupplierThumb;
    deals_count: number;
    deals: Array<{
        id: number;
        url_key?: string;
        label: string;
        contact_name: string | null;
        contact_state: string | null;
        is_primary: boolean;
        cbm?: number;
    }>;
    total_cbm?: number;
    clients: string[];
    already_exists: boolean;
};

export type ImportPreviewResponse = {
    selected_deals_count: number;
    shipment_count: number;
    skipped_groups_count: number;
    all_clients: string[];
    groups: ImportPreviewGroup[];
};

type Props = {
    selectedIds: number[];
};

export default function PloomesDealImportPreviewPanel({ selectedIds }: Props) {
    const sortedKey = useMemo(
        () => [...selectedIds].sort((a, b) => a - b).join(','),
        [selectedIds],
    );

    const { data, isLoading, isError } = useQuery({
        queryKey: ['ploomes-deals', 'import-preview', sortedKey],
        queryFn: async () => {
            const { data: payload } = await api.post<ImportPreviewResponse>(
                '/ploomes-deals/preview-import-request',
                { ids: selectedIds },
            );
            return payload;
        },
        enabled: selectedIds.length > 0,
        staleTime: 10_000,
    });

    if (selectedIds.length === 0) {
        return null;
    }

    return (
        <section className="rounded-xl border border-emerald-200 bg-white shadow-sm dark:border-emerald-900/40 dark:bg-gray-900">
            {/* Cabeçalho verde */}
            <div className="border-b border-emerald-100 bg-emerald-50/80 px-4 py-3 dark:border-emerald-900/30 dark:bg-emerald-950/30">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-400">
                    <PiTruck className="size-5" />
                    Prévia do embarque
                </h2>
                <p className="mt-1 text-xs text-emerald-800 dark:text-emerald-500">
                    {isLoading
                        ? 'Calculando agrupamento por fornecedor…'
                        : data
                          ? `${data.selected_deals_count} proposta(s) selecionada(s) → ${data.shipment_count} processo(s) de importação serão criados (1 por fornecedor). Cada processo mantém todos os pedidos e clientes abaixo.`
                          : 'Selecione propostas para ver o agrupamento.'}
                </p>
            </div>

            {isError ? (
                <p className="px-4 py-6 text-sm text-red-600 dark:text-red-400">
                    Não foi possível carregar a prévia.
                </p>
            ) : null}

            {data && !isLoading ? (
                <div className="space-y-4 p-4">
                    {/* Clientes da seleção */}
                    {data.all_clients.length > 0 ? (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/60">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Clientes na seleção ({data.all_clients.length})
                            </p>
                            <ClientAvatars names={data.all_clients} maxVisible={6} size="md" />
                        </div>
                    ) : (
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                            Nenhum cliente identificado nas propostas selecionadas.
                        </p>
                    )}

                    {/* Grupos por fornecedor */}
                    {data.groups.length === 0 ? (
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                            Não foi possível identificar fornecedor nos produtos das propostas selecionadas.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {data.groups.map((group) => (
                                <article
                                    key={group.supplier_label}
                                    className={[
                                        'rounded-lg border p-3',
                                        group.already_exists
                                            ? 'border-amber-200 bg-amber-50/50 dark:border-amber-900/50 dark:bg-amber-950/20'
                                            : 'border-gray-200 dark:border-gray-700 dark:bg-gray-800/40',
                                    ].join(' ')}
                                >
                                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            <SupplierAvatars suppliers={[group.supplier_display]} />
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                    {group.supplier_display.label}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {group.deals_count} pedido{group.deals_count !== 1 ? 's' : ''} neste embarque
                                                    {typeof group.total_cbm === 'number'
                                                        ? ` · ${group.total_cbm.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} m³`
                                                        : ''}
                                                </p>
                                            </div>
                                        </div>

                                        {group.already_exists ? (
                                            <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-400">
                                                <PiWarningCircle className="size-3.5" />
                                                Já existe processo
                                            </span>
                                        ) : (
                                            <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                                                Será criado
                                            </span>
                                        )}
                                    </div>

                                    <div className="grid gap-3 md:grid-cols-2">
                                        <div>
                                            <p className="mb-1 text-[10px] font-semibold uppercase text-gray-400 dark:text-gray-500">
                                                Pedidos
                                            </p>
                                            <ul className="space-y-1">
                                                {group.deals.map((deal) => (
                                                    <li key={deal.id} className="text-xs">
                                                        <Link
                                                            to={ploomesDealPath(deal)}
                                                            className="font-medium text-primary hover:underline"
                                                        >
                                                            {deal.label}
                                                        </Link>
                                                        {deal.is_primary && (
                                                            <span className="ms-1 text-[10px] text-gray-400 dark:text-gray-500">
                                                                (principal)
                                                            </span>
                                                        )}
                                                        {deal.contact_state && (
                                                            <span className="ms-1 text-gray-400 dark:text-gray-500">
                                                                · {deal.contact_state}
                                                            </span>
                                                        )}
                                                        {typeof deal.cbm === 'number' && (
                                                            <span className="ms-1 text-gray-500 dark:text-gray-400">
                                                                · {deal.cbm.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} m³
                                                            </span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="mb-1 text-[10px] font-semibold uppercase text-gray-400 dark:text-gray-500">
                                                Clientes deste embarque
                                            </p>
                                            <ClientAvatars names={group.clients} maxVisible={5} size="sm" />
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}

                    {data.skipped_groups_count > 0 && (
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                            {data.skipped_groups_count} fornecedor(es) já possuem processo aberto e serão
                            ignorados na criação.
                        </p>
                    )}
                </div>
            ) : null}

            {isLoading && (
                <div className="px-4 py-4">
                    <LoadingState variant="cards" count={3} />
                </div>
            )}
        </section>
    );
}
